import React, { useState, useEffect, useRef, useCallback } from "react";
import { Typography, Box, Tab, Tabs, Button, Modal, Divider } from '@mui/material';
import axios from "axios";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import GeneralInformationBox from "../../Utilities/NCProForms/GeneralInformationBox";
import AffectedItems from "../../Utilities/NCProForms/AffectedItems";
import NonConformanceDescription from "../../Utilities/NCProForms/NonConformanceDescription";
import AdditionalInformation from "../../Utilities/NCProForms/AdditionalInformation";
import PreventiveActionReview from "../../Utilities/NCProForms/PreventiveActionReview";
import CorrectiveActionReview from "../../Utilities/NCProForms/CorrectiveActionReview";
import NDRCEntry from "../../Utilities/NCProForms/NDRCEntry";
import PRCEntry from "../../Utilities/NCProForms/PRCEntry";
import PreventiveActions from "../../Utilities/NCProForms/PreventiveActions";
import Attachments from "../../Utilities/NCProForms/Attachments";
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ClosureVerification from "../../Utilities/NCProForms/ClosureVerification";
import IICA from "../../Utilities/NCProForms/IICA";
import ProgressStepper from "../../Utilities/ProgressStepper";
import { useDispatch, useSelector } from "react-redux";
import { ModalClose, Sheet } from "@mui/joy";
import CircularLoader from "../../Common/CircularLoader";
import { convertDBNameToUILabel } from "../../../Helpers/NCRHelpers";
import { closureSchema } from "../../../Helpers/schema";
import SRCEntry from "../../Utilities/NCProForms/SRCEntry";
import { fetchDashboardData } from "../../../Helpers/dashboardSlice";

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "14px",
          fontWeight: 400,
          color: "rgba(84, 84, 84, 1)",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
            opacity: 1,
          },
        },
      },
    },
  },
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function ClosureDetailsComponent({ content }) {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const accessToken = localStorage.getItem('NCProAccessToken');
  const { userData } = useSelector((state) => state.user);
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState();
  const closureRef = useRef(null);
  const dispatch = useDispatch();
  const selectedFinancialYear = useSelector(state => state.dashboard.selectedFinancialYear);

  useEffect(() => {
    if (tabValue === 1) {
      setOpen(true);
      if (closureRef.current) {
        closureRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [tabValue]);

  const closureFormData = {
    is_ncr_ready_for_closure: 'Yes',
    comments_for_closure: "",
    selected_value: 'Yes',
  }
  const [formData, setFormData] = useState(closureFormData);

  const handleClosureClick = async (reRoute) => {
    try {
      const filteredFormData = {
        is_ncr_ready_for_closure: formData.is_ncr_ready_for_closure || "Yes",
        comments_for_closure: formData.comments_for_closure || "",
        copq: formData.copq || 0,
        is_reroute: reRoute || false,
      };
      const requiredFields = formData.is_ncr_ready_for_closure === 'No'
        ? ["comments_for_closure"]
        : ["comments_for_closure", "copq"];
      let newErrors = {};
      requiredFields.forEach((field) => {
        if (!formData[field] || formData[field] === "") {
          newErrors[field] = `${convertDBNameToUILabel(field)} is required`;
        }
      });
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error("Please fill in all required fields.");
        return;
      }

      await closureSchema.parseAsync(filteredFormData);

      const response = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/closure/${formData.ncr_id}`,
        filteredFormData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

      if (response.data.success) {
        toast.success(response.data.message || "Form submitted Successful");
        navigate('/ncpro/closure');
        dispatch(fetchDashboardData({
          startDate: selectedFinancialYear.startDate,
          endDate: selectedFinancialYear.endDate
        }));
      } else {
        toast.error(response.data.message || "Failed to submit");
      }
    } catch (error) {
      console.error('Error during form submission:', error?.response?.data || error?.errors?.[0]?.message || "Something went wrong");
      toast.error(error?.response?.data || error?.errors?.[0]?.message || "Something went wrong");
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChange = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
    const field = Object.keys(updatedData)[0];
    setErrors((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  const getFromData = useCallback(async () => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem('NCProAccessToken');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/forms/${content.ncrId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setFormData(response.data.ncrForm);
    } catch (error) {
      console.error('Error fetching ncr:', error);
    } finally {
      setIsLoading(false);
    }
  }, [content.ncrId]);

  useEffect(() => {
    getFromData();
  }, [getFromData]);

  return (
    <>
      {isLoading ? (
        <CircularLoader label="NCRDetails" />
      ) : (
        <ThemeProvider theme={theme}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 14 }}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ pr: '4px' }}>
                <img
                  src="/images/leftArrow.png"
                  alt="Icon"
                  style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
                  onClick={() => navigate('/ncpro/closure')}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {formData.ncr_id} - Closure
                </Typography>
              </Box>
            </Box>
            <Box>
              <ProgressStepper
                status={
                  formData.corrective_action_required === 'No'
                    ? ['completed', 'completed', 'skipped', 'inProgress']
                    : ['completed', 'completed', 'completed', 'inProgress']
                }
              />
            </Box>
          </Box>
          <Box
            sx={{
              height: '75vh',
              overflow: 'auto',
              border: '1px solid #ccc',
              borderRadius: '8px',
              backgroundColor: '#fff',
              marginTop: '16px',
              '&::-webkit-scrollbar': {
                width: '7px',
                height: '7px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(211, 211, 211, 1)',
                borderRadius: '5px',
              }
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                  <Tab
                    label="General information"
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 0 ? '700' : '400',
                      color: tabValue === 0 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                  <Tab
                    label={formData.corrective_action_required === 'No' ? 'Containment action' : 'Containment action & Root cause'}
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 1 ? '700' : '400',
                      color: tabValue === 1 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                </Tabs>
              </Box>
              <CustomTabPanel value={tabValue} index={0}>
                <GeneralInformationBox content={{ formData: formData, inputDisable: true }} />
                <AffectedItems content={{ formData: formData, inputDisable: true }} />
                <NonConformanceDescription content={{ formData: formData, inputDisable: true }} />
                <AdditionalInformation content={{ label: "closure", formData: formData, inputDisable: true }} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <IICA content={{ formData: formData, inputDisable: true }} />
                <NDRCEntry content={{ formData: formData, inputDisable: true }} />
                <PRCEntry content={{ formData: formData, inputDisable: true }} />
                <SRCEntry content={{ formData: formData, inputDisable: true }} />
                <PreventiveActions content={{ formData: formData, inputDisable: true }} />
                <Attachments content={{ formData: formData, inputDisable: true }} />
                <CorrectiveActionReview content={{ formData: formData, inputDisable: true }} />
                <PreventiveActionReview content={{ formData: formData, inputDisable: true }} />
                <div ref={closureRef}>
                  <ClosureVerification content={{ label: "closure", formData: formData, errors: errors }} onChange={handleChange} />
                </div>
              </CustomTabPanel>
              {userData.roleCode === 'CLTADM' && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 3, py: 2,
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  zIndex: 10,
                  borderRadius: '0px 0px 10px 10px',
                  boxShadow: '0px -1px 4px 0px rgba(0, 0, 0, 0.15)'
                }}>
                  {formData.is_ncr_ready_for_closure === "No" &&
                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        color="neutral"
                        disabled={tabValue === 0}
                        sx={{
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          fontWeight: '400',
                          border: '1px solid rgba(14, 41, 102, 1) !important',
                          textTransform: 'none',
                          borderRadius: '10px',
                          color: 'rgba(14, 41, 102, 1) !important',
                          opacity: tabValue === 0 ? '70%' : '100%',
                          py: 0.8,
                        }}
                        onClick={() => handleClosureClick(true)}
                      >
                        Re-route
                      </Button>
                    </Box>
                  }
                  {formData.is_ncr_ready_for_closure !== "No" &&
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        color="neutral"
                        disabled={tabValue === 0}
                        sx={{
                          background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                          textTransform: 'none',
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          fontWeight: '700',
                          color: 'rgba(255, 255, 255, 1) !important',
                          borderRadius: '10px',
                          border: 'none !important',
                          py: 1,
                          px: 4,
                          opacity: tabValue === 0 ? '70%' : '100%',
                        }}
                        onClick={() => handleClosureClick(false)}
                      >
                        Close NC
                      </Button>
                    </Box>
                  }
                </Box>
              )}
            </Box>
          </Box>
        </ThemeProvider>
      )}
      {userData.roleCode !== 'CLTADM' && (
        <Box>
          <Modal
            aria-labelledby="modal-title"
            aria-describedby="modal-desc"
            open={open}
            onClose={() => {
              setOpen(false);
              setTabValue(0);
            }}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Sheet
              sx={{ maxWidth: 500, borderRadius: 'md', boxShadow: 'lg' }}
            >
              <ModalClose
                size='lg'
                onClick={() => {
                  setOpen(false);
                  setTabValue(0);
                }}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              />

              <Box sx={{ height: 32 }} />
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Box>
                <Typography className='NCR-awaiting-popup' sx={{ px: 4 }}>
                  Awaiting for closure comment.
                </Typography>
              </Box>
              <Box sx={{
                borderRadius: '0px 0px 10px 10px',
                boxShadow: '0px -1px 4px 0px #00000026',
                textAlign: 'right',
                px: 3,
                py: 1.5,
                mt: 3,
              }}>
                <Button
                  onClick={() => {
                    setOpen(false);
                    setTabValue(0);
                  }}
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                    color: 'white',
                    textTransform: 'none',
                    borderRadius: '7px',
                    px: 3,
                  }}
                >
                  Close
                </Button>
              </Box>
            </Sheet>
          </Modal>
        </Box>
      )}
    </>
  );
}
